/**
 * Pure transform functions for ImportData.
 *
 * Each function takes ImportData and returns a new ImportData with immutable
 * updates — new row/header objects are created, never mutated in place.
 *
 * Callers compose these via viewModel.updateImportData() which calls
 * setImportData once with the final result.
 */
import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'
import { ImportData, InternalColumns } from './importer-types'

function normalizeCompanyHash(hash: string | number): string {
  return String(hash)
}

// ---------------------------------------------------------------------------
// Row transforms (by company hash)
// ---------------------------------------------------------------------------

export function setMatchedChainByHash(
  data: ImportData,
  companyHash: string | number,
  chain: ChainSearchResult | null,
  manualMatch: boolean
): ImportData {
  const normalizedHash = normalizeCompanyHash(companyHash)

  return {
    ...data,
    rows: data.rows.map((row) =>
      normalizeCompanyHash(row[InternalColumns.COMPANY_HASH]) === normalizedHash
        ? {
            ...row,
            [InternalColumns.MATCHED_CHAIN]: chain,
            [InternalColumns.IS_CHAIN_MANUAL_MATCH]: manualMatch,
          }
        : row
    ),
  }
}

export function setMatchedDoorByHash(
  data: ImportData,
  companyHash: string | number,
  door: DoorSearchResult | null,
  manualMatch: boolean
): ImportData {
  const normalizedHash = normalizeCompanyHash(companyHash)

  return {
    ...data,
    rows: data.rows.map((row) =>
      normalizeCompanyHash(row[InternalColumns.COMPANY_HASH]) === normalizedHash
        ? {
            ...row,
            [InternalColumns.MATCHED_DOOR]: door,
            [InternalColumns.IS_DOOR_MANUAL_MATCH]: manualMatch,
          }
        : row
    ),
  }
}

export function setIsCustomCompanyByHash(
  data: ImportData,
  companyHashes: Array<string | number>,
  isCustom: boolean
): ImportData {
  const hashSet = new Set(companyHashes.map(normalizeCompanyHash))

  return {
    ...data,
    rows: data.rows.map((row) =>
      hashSet.has(normalizeCompanyHash(row[InternalColumns.COMPANY_HASH]))
        ? { ...row, [InternalColumns.IS_CUSTOM_COMPANY]: isCustom }
        : row
    ),
  }
}

export function setCustomCompanyCategoryByHash(
  data: ImportData,
  companyHashes: Array<string | number>,
  categoryId: number,
  subcategoryId: number | null
): ImportData {
  const hashSet = new Set(companyHashes.map(normalizeCompanyHash))

  return {
    ...data,
    rows: data.rows.map((row) =>
      hashSet.has(normalizeCompanyHash(row[InternalColumns.COMPANY_HASH]))
        ? {
            ...row,
            [InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]: categoryId,
            [InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]: subcategoryId,
            [InternalColumns.IS_CUSTOM_COMPANY]: true,
          }
        : row
    ),
  }
}

export function setReadyByHash(
  data: ImportData,
  companyHashes: Array<string | number>,
  ready: boolean
): ImportData {
  const hashSet = new Set(companyHashes.map(normalizeCompanyHash))

  return {
    ...data,
    rows: data.rows.map((row) =>
      hashSet.has(normalizeCompanyHash(row[InternalColumns.COMPANY_HASH]))
        ? { ...row, [InternalColumns.READY]: ready }
        : row
    ),
  }
}

// ---------------------------------------------------------------------------
// Per-row action transforms (for granular custom/exclude control)
// ---------------------------------------------------------------------------

/**
 * Set a single company as custom with the specified type.
 * This is used for per-row dropdown selection.
 */
export function setAsCustomCompany(
  data: ImportData,
  companyHash: string | number,
  categoryId: number,
  subcategoryId: number
): ImportData {
  const normalizedHash = normalizeCompanyHash(companyHash)

  return {
    ...data,
    rows: data.rows.map((row) =>
      normalizeCompanyHash(row[InternalColumns.COMPANY_HASH]) === normalizedHash
        ? {
            ...row,
            [InternalColumns.IS_CUSTOM_COMPANY]: true,
            [InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]: categoryId,
            [InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]: subcategoryId,
          }
        : row
    ),
  }
}

/**
 * Set a single company as excluded (not custom, will be skipped).
 * Clears any custom company settings.
 */
export function setAsExcluded(
  data: ImportData,
  companyHash: string | number
): ImportData {
  const normalizedHash = normalizeCompanyHash(companyHash)

  return {
    ...data,
    rows: data.rows.map((row) =>
      normalizeCompanyHash(row[InternalColumns.COMPANY_HASH]) === normalizedHash
        ? {
            ...row,
            [InternalColumns.IS_CUSTOM_COMPANY]: false,
            [InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]: null,
            [InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]: null,
          }
        : row
    ),
  }
}

/**
 * Set multiple companies as custom with the specified type.
 * Used for bulk actions.
 */
export function setAsCustomCompanyBulk(
  data: ImportData,
  companyHashes: Array<string | number>,
  categoryId: number,
  subcategoryId: number
): ImportData {
  const hashSet = new Set(companyHashes.map(normalizeCompanyHash))

  return {
    ...data,
    rows: data.rows.map((row) =>
      hashSet.has(normalizeCompanyHash(row[InternalColumns.COMPANY_HASH]))
        ? {
            ...row,
            [InternalColumns.IS_CUSTOM_COMPANY]: true,
            [InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]: categoryId,
            [InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]: subcategoryId,
          }
        : row
    ),
  }
}

/**
 * Set multiple companies as excluded.
 * Used for bulk actions.
 * Clears matches (chain/door) and custom company settings.
 */
export function setAsExcludedBulk(
  data: ImportData,
  companyHashes: Array<string | number>
): ImportData {
  const hashSet = new Set(companyHashes.map(normalizeCompanyHash))

  return {
    ...data,
    rows: data.rows.map((row) =>
      hashSet.has(normalizeCompanyHash(row[InternalColumns.COMPANY_HASH]))
        ? {
            ...row,
            // Clear matches
            [InternalColumns.MATCHED_CHAIN]: null,
            [InternalColumns.MATCHED_DOOR]: null,
            [InternalColumns.IS_CHAIN_MANUAL_MATCH]: false,
            [InternalColumns.IS_DOOR_MANUAL_MATCH]: false,
            // Clear custom company settings
            [InternalColumns.IS_CUSTOM_COMPANY]: false,
            [InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]: null,
            [InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]: null,
          }
        : row
    ),
  }
}

// ---------------------------------------------------------------------------
// Row transforms (by row ID)
// ---------------------------------------------------------------------------

export function setReadyByRowId(
  data: ImportData,
  rowIds: string[],
  ready: boolean
): ImportData {
  const idSet = new Set(rowIds)
  return {
    ...data,
    rows: data.rows.map((row) =>
      idSet.has(row[InternalColumns.ROW_ID])
        ? { ...row, [InternalColumns.READY]: ready }
        : row
    ),
  }
}

// ---------------------------------------------------------------------------
// Header transforms
// ---------------------------------------------------------------------------

export function setHeaderMapping(
  data: ImportData,
  originalHeader: string,
  mappedColumn: string
): ImportData {
  return {
    ...data,
    headers: data.headers.map((h) =>
      h.original === originalHeader ? { ...h, mapped: mappedColumn } : h
    ),
  }
}

// ---------------------------------------------------------------------------
// Persistence transforms
// ---------------------------------------------------------------------------

/**
 * Prepare data for persistence by marking matched companies as READY.
 * This is called in the OPERATING.preparing phase before saving to backend.
 *
 * Logic: If a company has a matched chain, matched door, or is a custom company,
 * and it's not already marked as READY, mark it as READY.
 */
export function prepareDataForPersist(data: ImportData): ImportData {
  const updatedRows = data.rows.map((row) => {
    const hasMatch =
      row[InternalColumns.MATCHED_CHAIN] ||
      row[InternalColumns.MATCHED_DOOR] ||
      row[InternalColumns.IS_CUSTOM_COMPANY]

    if (hasMatch && !row[InternalColumns.READY]) {
      return { ...row, [InternalColumns.READY]: true }
    }
    return row
  })

  return { ...data, rows: updatedRows }
}
